import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { listTradeFairs, TradeFairState, type TradeFair } from '@/api/tradeFairs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'

/** Radix has no value for "no filter", and an empty string is not one it accepts. */
const ALL = 'ALL'

/**
 * Narrowing the lead list to one fair. After three days at a stand the fair is the coarsest cut
 * there is, and the one somebody reaches for before searching for a name.
 *
 * Nothing is preselected: the list opens on everything the user may see, and the running fair
 * being preselected here would hide leads without saying so.
 */
export function TradeFairFilter({
  value,
  onChange,
}: {
  value: string | null
  onChange: (id: string | null) => void
}) {
  const { t } = useTranslation()
  const [fairs, setFairs] = useState<TradeFair[]>([])

  useEffect(() => {
    const controller = new AbortController()
    listTradeFairs({ size: 100 }, controller.signal)
      .then((page) => !controller.signal.aborted && setFairs(page.tradeFairs))
      .catch(() => {
        // The filter stays on "all fairs", which is what the list already shows.
      })
    return () => controller.abort()
  }, [])

  return (
    <Select
      value={value ?? ALL}
      onValueChange={(next) => next && onChange(next === ALL ? null : next)}
    >
      <SelectTrigger className="w-48" aria-label={t('leads.tradeFairFilterLabel')}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ALL}>{t('leads.tradeFairFilterAll')}</SelectItem>
        {fairs.map((fair) => (
          <SelectItem key={fair.id} value={fair.id} textValue={fair.name}>
            {fair.name}
            {fair.state === TradeFairState.RUNNING && ` · ${t('tradeFairs.state.RUNNING')}`}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}
